import React, { useEffect, useRef, useState } from "react";
import { X, Download, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import RankShareCard from "./RankShareCard";
import RankBadge from "./RankBadge";
import SharePopover from "./SharePopover";
import { buildShareText, SHARE_URL } from "../lib/share";
import { playPlateClang } from "../lib/clang";

export default function RankUpModal({ rankUp, expEarned, totalExp, onClose }) {
    const [visible, setVisible] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const cardRef = useRef(null);

    useEffect(() => {
        if (rankUp) {
            const t = setTimeout(() => setVisible(true), 50);
            // Play the gym plate-slam the instant the user sees the card.
            // Runs through Web Audio (synthesised — no external asset). If the
            // browser blocks autoplay, the catch in playPlateClang silently
            // no-ops.
            try { playPlateClang(); } catch (e) { /* ignore */ }
            return () => clearTimeout(t);
        }
    }, [rankUp]);

    if (!rankUp) return null;

    const shareText = buildShareText(rankUp.new, totalExp ?? (rankUp.new.min_exp + (expEarned || 0)));

    const downloadCard = async () => {
        if (!cardRef.current) return null;
        // Wait one frame so fonts/icons settle
        await new Promise(r => requestAnimationFrame(r));
        return toPng(cardRef.current, {
            cacheBust: true,
            pixelRatio: 1,
            width: 1080,
            height: 1080,
            backgroundColor: "#0a0a0a",
        });
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const dataUrl = await downloadCard();
            if (!dataUrl) throw new Error("no card");
            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = `hypergains-rank-${rankUp.new.id}-${rankUp.new.name.toLowerCase().replace(/\s+/g, "-")}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success("Card saved — share it on your story.");
        } catch (e) {
            toast.error("Could not save the card. Try again in a sec.");
        } finally {
            setDownloading(false);
        }
    };

    const getShareFile = async () => {
        const dataUrl = await downloadCard();
        if (!dataUrl) return null;
        const blob = await (await fetch(dataUrl)).blob();
        return new File([blob], `hypergains-rank-${rankUp.new.id}.png`, { type: "image/png" });
    };

    return (
        <>
            {/* Off-screen 1080x1080 share card used for PNG export */}
            <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none", opacity: 0 }} aria-hidden="true">
                <RankShareCard ref={cardRef} rank={rankUp.new} totalExp={totalExp ?? (rankUp.new.min_exp + (expEarned || 0))} />
            </div>

            <div
                className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
                data-testid="rank-up-modal"
                onClick={(e) => {
                    // Click on the dark backdrop (outside the card) closes the
                    // modal. The inner card stops propagation below so clicks
                    // inside it don't dismiss.
                    if (e.target === e.currentTarget) onClose?.();
                }}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    className={`relative w-full max-w-md transform transition-all duration-500 ${visible ? "scale-100 opacity-100" : "scale-90 opacity-0"}`}
                >
                    {/* Intentionally small + subtle X — the share button is the
                        primary action. Users can also tap the dark backdrop. */}
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="rank-up-close"
                        className="absolute right-2 top-2 z-10 rounded-full p-1 text-white/40 hover:text-white/80"
                    >
                        <X className="h-3 w-3" />
                    </button>

                    <div className="relative overflow-hidden border-2 border-accent-red bg-[#0a0a0a] text-white">
                        {/* Soft edge glow only — the body of the card is a flat
                            near-black so all text reads with high contrast. */}
                        <div className="pointer-events-none absolute inset-x-0 -top-32 h-64 bg-gradient-to-b from-accent-red/35 to-transparent blur-3xl" />
                        <div className="pointer-events-none absolute inset-x-0 -bottom-32 h-64 bg-gradient-to-t from-accent-red/20 to-transparent blur-3xl" />

                        <div className="relative px-8 pb-8 pt-10 text-center">
                            <div className="label-eyebrow text-accent-red animate-pulse">Rank up · congratulations</div>

                            {/* Hero insignia — the actual rank badge instead of a
                                generic trophy. Wrapped in a faint accent ring. */}
                            <div className="relative mx-auto mt-5 inline-flex items-center justify-center" data-testid="rank-up-insignia">
                                <div className="absolute inset-0 -m-3 rounded-full bg-accent-red/15 blur-2xl" />
                                <div className="relative rounded-md border border-accent-red/40 bg-black/40 p-2 shadow-[0_0_30px_-6px_rgba(255,59,48,0.6)]">
                                    <RankBadge rankId={rankUp.new.id} size="lg" />
                                </div>
                            </div>

                            <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.2em] text-white">
                                <span className="text-white/60">{rankUp.old.name}</span>
                                <span className="mx-2 text-accent-red">→</span>
                                <span className="text-accent-red">{rankUp.new.tier} Tier</span>
                            </p>
                            <h2
                                className="mt-2 font-display text-4xl font-black uppercase leading-[1.05] text-white sm:text-5xl"
                                data-testid="rank-up-name"
                            >
                                {rankUp.new.name}
                            </h2>
                            {rankUp.new.tagline && (
                                <p
                                    className="mx-auto mt-4 max-w-sm rounded-sm border border-white/10 bg-black/50 px-4 py-3 text-sm italic leading-snug text-white sm:text-base"
                                    data-testid="rank-up-tagline"
                                >
                                    "{rankUp.new.tagline}"
                                </p>
                            )}
                            {expEarned != null && (
                                <p className="mt-5 text-sm text-white/90">
                                    You earned <span className="font-display text-base font-black text-accent-red">+{expEarned} EXP</span> this session.
                                </p>
                            )}
                            <div className="mt-6 flex flex-col items-stretch gap-2 sm:flex-row sm:justify-center">
                                <SharePopover
                                    text={shareText}
                                    url={SHARE_URL}
                                    getFile={getShareFile}
                                    label="Share your rank"
                                    size="lg"
                                    variant="primary"
                                    testIdPrefix="rank-up-share"
                                />
                                <button
                                    onClick={handleDownload}
                                    disabled={downloading}
                                    data-testid="rank-up-download"
                                    className="flex items-center justify-center gap-2 rounded-sm border border-white/20 bg-white/5 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-white/10 disabled:opacity-60"
                                >
                                    {downloading ? <ImageIcon className="h-4 w-4 animate-pulse" /> : <Download className="h-4 w-4" />}
                                    {downloading ? "Saving" : "Save card"}
                                </button>
                            </div>
                            <p className="mt-3 text-[11px] text-white/55">1080×1080 PNG · perfect for IG/TikTok stories · tap outside the card to close</p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
